import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/axios';
import { useUIStore } from '@/stores/uiStore';

// JSON:API content type required for all mutations
const JSONAPI = { 'Content-Type': 'application/vnd.api+json' };

// Drupal JSON:API root returns absolute hrefs — strip origin so requests go through Vite proxy
function toProxyPath(href) {
  try {
    const u = new URL(href);
    return u.pathname + u.search;
  } catch {
    return href;
  }
}

// JSON:API resource type for order items — must match the Drupal order item bundle.
// Variation bundle is impact_subscription_variation → order item bundle is likely impact_subscription_order.

function normalizeVariation(v, bundle) {
  const sku = v.attributes?.sku ?? '';
  const skuUpper = sku.toUpperCase();
  const hasFreqSuffix = skuUpper.endsWith('-ANNUAL') || skuUpper.endsWith('-MONTHLY');
  const period = skuUpper.endsWith('-ANNUAL') ? 'annual' : 'monthly';
  const periodLabel = period === 'annual' ? '/yr' : '/mo';

  const licenseRole = v.attributes?.license_type?.target_plugin_configuration?.license_role ?? '';
  let tier = null;
  if (licenseRole.includes('starter'))         tier = 'Starter';
  else if (licenseRole.includes('growth'))     tier = 'Growth';
  else if (licenseRole.includes('network'))    tier = 'Network';
  else if (licenseRole.includes('scale'))      tier = 'Scale';
  else if (licenseRole.includes('enterprise')) tier = 'Enterprise';

  if (!tier) {
    const skuLower = sku.toLowerCase();
    if (skuLower.includes('starter'))         tier = 'Starter';
    else if (skuLower.includes('growth'))     tier = 'Growth';
    else if (skuLower.includes('network'))    tier = 'Network';
    else if (skuLower.includes('scale'))      tier = 'Scale';
    else if (skuLower.includes('enterprise')) tier = 'Enterprise';
  }

  const price = v.attributes?.price ?? null;
  const featureSet = v.attributes?.field_feature_set;
  const features = featureSet?.processed ?? featureSet?.value ?? null;

  return {
    id: v.id,
    type: v.type,   // canonical JSON:API type string, e.g. "product-variation--impact-subscription"
    bundle,
    sku: v.attributes?.sku ?? '',
    title: v.attributes?.title ?? '',
    tier,
    period,
    hasFreqSuffix,
    price,
    periodLabel,
    licenseRole,
    features,
  };
}

// Extract Stripe client_secret from wherever commerce_stripe puts it
function extractClientSecret(order) {
  if (!order) return null;
  // commerce_api surfaces it on the root checkout response or the order's meta
  return (
    order.meta?.payment_intent_client_secret ??
    order.meta?.stripe_client_secret ??
    order.data?.meta?.payment_intent_client_secret ??
    order.data?.meta?.stripe_client_secret ??
    order.data?.attributes?.payment_instrument?.client_secret ??
    null
  );
}

// ─── Product discovery ────────────────────────────────────────────────────────

export function useImpactProduct() {
  return useQuery({
    queryKey: ['impactProduct'],
    queryFn: async ({ signal }) => {
      const r = await apiClient.get('/jsonapi/commerce_product/impact_subscription', {
        params: { include: 'variations' },
        signal,
      });

      const product = r.data?.data?.[0];
      if (!product) return null;

      const bundle = product.type?.replace(/^commerce_product--/, '') ?? 'impact_subscription';
      const included = r.data.included ?? [];
      const variationRefs = product.relationships?.variations?.data ?? [];
      const variations = variationRefs
        .map((ref) => included.find((i) => i.id === ref.id))
        .filter(Boolean)
        .filter((v) => v.attributes?.status !== false)
        .map((v) => normalizeVariation(v, bundle));
      console.log('[useImpactProduct] variations:', variations.map((v) => ({ sku: v.sku, tier: v.tier, period: v.period })));

      return { id: product.id, bundle, title: product.attributes?.title, variations };
    },
    staleTime: 10 * 60_000,
    retry: 1,
  });
}

// ─── Cart ─────────────────────────────────────────────────────────────────────
// Requires drupal/commerce_api module to be enabled.

// GET /jsonapi/carts — returns all active cart orders for the current session
export function useCart() {
  return useQuery({
    queryKey: ['cart'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/jsonapi/carts', { signal });
      return data.data?.[0] ?? null;
    },
    staleTime: 0,
  });
}

// POST /jsonapi/cart/add — add a product variation to the cart.
// commerce_api creates/reuses the draft order automatically; no manual order creation needed.
// Returns { orderUuid, orderType }
export function useAddToCart() {
  const qc = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async ({ variationId, variationType, quantity = 1 }) => {
      // commerce_api cart/add expects the purchased entity (variation) directly —
      // NOT an order-item wrapper. The server creates the order item automatically.
      // quantity goes in meta, not attributes.
      const { data } = await apiClient.post(
        '/jsonapi/cart/add',
        {
          data: [
            {
              type: variationType,
              id: variationId,
              meta: { quantity },
            },
          ],
        },
        { headers: JSONAPI },
      );

      const item = data.data?.[0];
      const orderRef = item?.relationships?.order_id?.data ?? null;
      return {
        orderUuid: orderRef?.id ?? null,
        orderType: orderRef?.type ?? null,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: (err) => {
      console.error('[cart/add error]', JSON.stringify(err?.response?.data, null, 2));
      addToast({
        title: 'Unable to add to cart',
        description: err?.response?.data?.errors?.[0]?.detail ?? 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// ─── Checkout (commerce_api module) ──────────────────────────────────────────

// GET /jsonapi/checkout/{order_uuid}
// Returns the order resource including Stripe client_secret in meta once payment is set up.
export function useCheckout(orderUuid) {
  return useQuery({
    queryKey: ['checkout', orderUuid],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get(`/jsonapi/checkout/${orderUuid}`, { signal });
      return data;
    },
    enabled: !!orderUuid,
    staleTime: 0,
  });
}

// PATCH /jsonapi/checkout/{order_uuid}
// Update order details (email, billing profile) as the customer moves through checkout.
// Requires orderType — sourced from the checkout GET response (checkoutOrder.data.type).
export function useUpdateCheckout() {
  const qc = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async ({ orderUuid, orderType, email }) => {
      const { data } = await apiClient.patch(
        `/jsonapi/checkout/${orderUuid}`,
        {
          data: {
            type: orderType,
            id: orderUuid,
            attributes: { email },
          },
        },
        { headers: JSONAPI },
      );
      // Return the full response body — client_secret may be in root meta or data.meta
      return data;
    },
    onSuccess: (_, { orderUuid }) => {
      qc.invalidateQueries({ queryKey: ['checkout', orderUuid] });
    },
    onError: (err) => {
      addToast({
        title: 'Checkout error',
        description: err?.response?.data?.errors?.[0]?.detail ?? 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}

// POST /jsonapi/checkout/{order_uuid}/payment
// Transitions the order after Stripe has confirmed the PaymentIntent client-side.
// commerce_api uses /payment (not /complete) to finalise the order.
export function useCompleteOrder() {
  const qc = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async ({ orderUuid, paymentOption = null }) => {
      // For $0 / free-trial orders: send {"data": null} — no payment instrument needed.
      // For paid orders: paymentOption should be { type, id } from payment_options[].
      const { data } = await apiClient.post(
        `/jsonapi/checkout/${orderUuid}/payment`,
        { data: paymentOption },
        { headers: JSONAPI },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: (err) => {
      addToast({
        title: 'Order completion failed',
        description:
          err?.response?.data?.message ??
          err?.response?.data?.errors?.[0]?.detail ??
          'Unable to complete order. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

export { extractClientSecret };
